/**
 * 陸送員スケジュール編集権限設定フォームコンポーネント
 * FL-115: 陸送員スケジュール編集権限設定機能
 */

import React from 'react';
import { DriverScheduleEditPermission } from '../../types';
import Button from '../ui/Button';
import logger from '../../utils/logger';
import {
  ShieldCheckIcon,
  PencilIcon,
  LockClosedIcon,
  LockOpenIcon,
} from '@heroicons/react/24/outline';

interface DriverScheduleEditPermissionFormProps {
  permission: DriverScheduleEditPermission;
  onChange: (permission: DriverScheduleEditPermission) => void;
  onSave: () => Promise<void>;
  isLoading?: boolean;
}

const DriverScheduleEditPermissionForm: React.FC<DriverScheduleEditPermissionFormProps> = ({
  permission,
  onChange,
  onSave,
  isLoading = false,
}) => {
  const handlePermissionChange = (newPermission: DriverScheduleEditPermission) => {
    logger.info('🔄 スケジュール編集権限設定変更', {
      from: permission,
      to: newPermission,
    });
    onChange(newPermission);
  };

  const handleSave = async () => {
    logger.info('🔄 スケジュール編集権限設定保存開始', { permission });
    await onSave();
  };

  return (
    <div className='space-y-6'>
      <div>
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
          陸送員スケジュール編集権限設定
        </h3>
        <p className='text-sm text-gray-600 dark:text-gray-300'>
          陸送員が自分のスケジュール（出勤・勤怠管理）を編集できる権限を設定します。
          管理者・配車管理者は常に編集可能です。
        </p>
      </div>

      <div className='space-y-4'>
        {/* 管理者のみ設定 */}
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
            permission === DriverScheduleEditPermission.ADMIN_ONLY
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
          onClick={() => handlePermissionChange(DriverScheduleEditPermission.ADMIN_ONLY)}
        >
          <div className='flex items-start space-x-3'>
            <div
              className={`mt-1 ${
                permission === DriverScheduleEditPermission.ADMIN_ONLY
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <ShieldCheckIcon className='h-5 w-5' />
            </div>
            <div className='flex-1'>
              <div className='flex items-center space-x-2'>
                <input
                  type='radio'
                  checked={permission === DriverScheduleEditPermission.ADMIN_ONLY}
                  onChange={() => handlePermissionChange(DriverScheduleEditPermission.ADMIN_ONLY)}
                  className='h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500'
                />
                <label className='text-sm font-medium text-gray-900 dark:text-white'>
                  管理者のみ編集可能
                </label>
              </div>
              <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>
                陸送員のスケジュール編集は管理者・配車管理者のみ可能。
                勤怠管理を厳格にコントロールしたい場合におすすめします。
              </p>
            </div>
          </div>
        </div>

        {/* 陸送員自己編集可能設定 */}
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
            permission === DriverScheduleEditPermission.DRIVER_SELF_EDIT
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
          }`}
          onClick={() => handlePermissionChange(DriverScheduleEditPermission.DRIVER_SELF_EDIT)}
        >
          <div className='flex items-start space-x-3'>
            <div
              className={`mt-1 ${
                permission === DriverScheduleEditPermission.DRIVER_SELF_EDIT
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <PencilIcon className='h-5 w-5' />
            </div>
            <div className='flex-1'>
              <div className='flex items-center space-x-2'>
                <input
                  type='radio'
                  checked={permission === DriverScheduleEditPermission.DRIVER_SELF_EDIT}
                  onChange={() =>
                    handlePermissionChange(DriverScheduleEditPermission.DRIVER_SELF_EDIT)
                  }
                  className='h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500'
                />
                <label className='text-sm font-medium text-gray-900 dark:text-white'>
                  陸送員自己編集可能
                </label>
              </div>
              <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>
                陸送員が自分のスケジュール（出勤・休暇予定等）を自由に編集可能。
                柔軟な勤怠管理を行いたい場合におすすめします。
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 現在の設定表示 */}
      <div className='bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4'>
        <div className='flex items-center space-x-2'>
          {permission === DriverScheduleEditPermission.ADMIN_ONLY ? (
            <LockClosedIcon className='h-4 w-4 text-gray-500 dark:text-gray-400' />
          ) : (
            <LockOpenIcon className='h-4 w-4 text-gray-500 dark:text-gray-400' />
          )}
          <span className='text-sm text-gray-600 dark:text-gray-300'>
            現在の設定:
            <strong className='ml-1'>
              {permission === DriverScheduleEditPermission.ADMIN_ONLY
                ? '管理者のみ編集可能'
                : '陸送員自己編集可能'}
            </strong>
          </span>
        </div>
      </div>

      {/* 保存ボタン */}
      <div className='flex justify-end'>
        <Button variant='primary' onClick={handleSave} disabled={isLoading} className='px-6'>
          {isLoading ? '保存中...' : '設定を保存'}
        </Button>
      </div>
    </div>
  );
};

export default DriverScheduleEditPermissionForm;
